import { DynamicFormFullContextProvider } from "./Providers/DynamicFormFullContextProvider";
import { DynamicFormContext, DynamicFormSchema, IDynamicFormError, IDynamicFormState, IDynamicFormValue } from "./DynamicFormContracts";
import useDynamicForm from "./Hooks/useDynamicForm";
import styles from "./DynamicForm.module.css";

export interface IDynamicFormProps<T> {
  schema: DynamicFormSchema<T>;
  context: DynamicFormContext;
  disabled?: boolean;
  loading?: boolean;
  reset?: boolean;
  className?: string;
  onChange?: (state: IDynamicFormState<T>) => void;
  onSubmit?: (values: IDynamicFormValue<T>, anyError: boolean, errors: IDynamicFormError<T>) => void;
  forceFieldsColumns?: number;
}

function DynamicForm<T>({
  schema,
  context,
  disabled,
  loading,
  className,
  onChange,
  onSubmit,
  reset,
  forceFieldsColumns,
}: IDynamicFormProps<T>) {

  const { inputProps, state, handleSubmit, anyError } = useDynamicForm({
    initValues: schema.initValues,
    onChange,
    onSubmit,
    disabled,
    reset,
  });

  const SubmitField = context.SubmitField;

  return (
    <DynamicFormFullContextProvider
      context={{
        state,
        schema,
        ...context,
      }}
    >
      <div
        className={styles.form + (className ? ` ${className}` : "")}
        style={{ gridTemplateColumns: `repeat(${schema.columns}, 1fr)` }}
      >
        {schema.fields.map((f, i) => {
          const Component = context.fieldsMap[f.type];
          if (!Component) {
            throw new Error(`Not found '${f.type}' DynamicField, include it in fieldsMap `);
          }
          return (
            <div key={i} className={styles.fieldContainer} style={{ gridColumn: `auto / span ${forceFieldsColumns ?? f.columns}` }}>
              <Component
                {...inputProps(f.name)}
                label={f.label}
                attributes={f.attributes}
                requiredIndicator={context.requiredIndicator}
              />
            </div>
          )
        })}
        {!!SubmitField && (
              <div
                className={styles.fieldContainer}
                style={{
                  gridColumn: `auto / span ${schema.submitFieldColumns ?? schema.columns
                    }`,
                }}
              >
                <SubmitField
                  loading={loading}
                  disabled={
                    disabled || (anyError && schema.disableSubmitOnError === true)
                  }
                  label={schema.submitLabel}
                  onSubmit={handleSubmit}
                />
              </div>
            )}
      </div>
    </DynamicFormFullContextProvider>
  );
}

export default DynamicForm;
